import { APP_CONFIG } from '@/constants/app';
import { unwrapApiData } from '@/utils/api-response';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';
import type {
  ActionProposee,
  MessageAssistant,
} from '../types/assistant.types';

const assistantApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/assistant',
  // Une demande = un appel au modèle + un service : rarement plus de 10 s,
  // mais une liste d'éditions passe par le robot navigateur.
  timeout: 90_000,
});
tokenManager.setupInterceptors(assistantApi);

/** Ce que le serveur sait faire : rien n'est proposé à l'écran qu'il refuserait. */
export interface Disponibilite {
  disponible: boolean;
  /** La modification des coordonnées dans OpenPaye est-elle ouverte ? */
  ecriture: boolean;
}

/** L'annonce à la première connexion : reste-t-elle à valider, et quelle version ? */
export interface EtatAnnonce {
  aValider: boolean;
  version: string;
}

interface ReponseMessage {
  uuid: string;
  conversationUuid: string;
  texte: string;
  resultats?: MessageAssistant['resultats'];
  action?: ActionProposee;
  limite?: boolean;
}

/**
 * Le vrai service : l'écran parle au serveur, qui choisit l'outil et
 * l'exécute sous l'identité de l'utilisateur connecté.
 */
export const assistantService = {
  async disponibilite(): Promise<Disponibilite> {
    const r = await assistantApi.get<Disponibilite>('/disponibilite');
    const d = unwrapApiData(r.data);
    return { disponible: !!d.disponible, ecriture: !!d.ecriture };
  },

  /** Cinq secondes au plus : cet appel ne doit jamais retenir l'application. */
  async annonce(): Promise<EtatAnnonce> {
    const r = await assistantApi.get<EtatAnnonce>('/annonce', {
      timeout: 5_000,
    });
    const d = unwrapApiData(r.data);
    return { aValider: !!d.aValider, version: String(d.version ?? '') };
  },

  /** Idempotent côté serveur : un double clic ne crée pas deux preuves. */
  async validerAnnonce(): Promise<EtatAnnonce> {
    const r = await assistantApi.post<EtatAnnonce>('/annonce/valider');
    const d = unwrapApiData(r.data);
    return { aValider: !!d.aValider, version: String(d.version ?? '') };
  },

  async envoyer(
    texte: string,
    conversationUuid: string | undefined,
    dossierUuid: string | undefined
  ): Promise<{ conversationUuid: string; message: MessageAssistant }> {
    const r = await assistantApi.post<ReponseMessage>('/messages', {
      texte,
      conversationUuid,
      dossierUuid,
    });
    const data = unwrapApiData(r.data);
    return {
      conversationUuid: data.conversationUuid,
      message: {
        id: data.uuid,
        auteur: 'assistant',
        texte: data.texte,
        resultats: data.resultats,
        action: data.action,
        limite: data.limite,
      },
    };
  },

  async lireAction(uuid: string): Promise<ActionProposee> {
    const r = await assistantApi.get<ActionProposee>(`/actions/${uuid}`);
    return unwrapApiData(r.data);
  },

  async confirmer(uuid: string): Promise<ActionProposee> {
    const r = await assistantApi.post<ActionProposee>(
      `/actions/${uuid}/confirmer`
    );
    return unwrapApiData(r.data);
  },

  async annuler(uuid: string): Promise<ActionProposee> {
    const r = await assistantApi.post<ActionProposee>(
      `/actions/${uuid}/annuler`
    );
    return unwrapApiData(r.data);
  },

  /** Un fichier servi par l'API sous l'identité de l'utilisateur (un bulletin). */
  async telecharger(chemin: string, nomFichier: string): Promise<void> {
    // Le jeton ne part que vers l'API de l'assistant, jamais vers un chemin
    // absolu ou un autre domaine, quoi que renvoie le serveur.
    if (
      !/^\/assistant\/bulletins\/[\w-]+\/[\w-]+\/\d{4}\/\d{1,2}(\?[\w=&%.-]*)?$/.test(
        chemin
      )
    ) {
      throw new Error('Chemin de téléchargement inattendu');
    }
    const r = await assistantApi.get(chemin.replace(/^\/assistant/, ''), {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nomFichier;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
